import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';

import { LabTestingResult, LabTestingResultSearchCommand } from '../../models/lab-testing';
import { ApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class LabTestingResultProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.productionApi + '/api/v1/lab-testing-result';

  public getAll(): Observable<ApiResponse<LabTestingResult[]>> {
    return this.httpClient.get<ApiResponse<LabTestingResult[]>>(`${this.baseUrl}/all`);
  }

  public getById(id: string): Observable<ApiResponse<LabTestingResult>> {
    return this.httpClient.get<ApiResponse<LabTestingResult>>(`${this.baseUrl}/${id}`);
  }

  public create(result: LabTestingResult): Observable<ApiResponse<LabTestingResult>> {
    return this.httpClient.post<ApiResponse<LabTestingResult>>(`${this.baseUrl}`, result);
  }

  public update(result: LabTestingResult): Observable<ApiResponse<LabTestingResult>> {
    return this.httpClient.put<ApiResponse<LabTestingResult>>(`${this.baseUrl}/${result.id}`, result);
  }

  public delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  public search(searchCommand: LabTestingResultSearchCommand): Observable<ApiResponse<LabTestingResult[]>> {
    return this.httpClient.post<ApiResponse<LabTestingResult[]>>(`${this.baseUrl}/search`, searchCommand);
  }

  public createFromStandard(productionCardReference: string, labTestingStandardId: string): Observable<ApiResponse<LabTestingResult>> {
    const params = new HttpParams()
      .set('productionCardReference', productionCardReference)
      .set('labTestingStandardId', labTestingStandardId);

    return this.httpClient.post<ApiResponse<LabTestingResult>>(`${this.baseUrl}/create-from-standard`, {}, { params });
  }

  public getByProductionCardReference(productionCardReference: string): Observable<ApiResponse<LabTestingResult[]>> {
    return this.httpClient.get<ApiResponse<LabTestingResult[]>>(`${this.baseUrl}/by-production-card/${productionCardReference}`);
  }

  public hasPassedTest(productionCardReference: string): Observable<ApiResponse<boolean>> {
    return this.httpClient.get<ApiResponse<boolean>>(`${this.baseUrl}/has-passed/${productionCardReference}`);
  }

  public updateTestResult(id: string, passed: boolean, comments?: string): Observable<ApiResponse<LabTestingResult>> {
    let params = new HttpParams().set('passed', passed.toString());

    if (comments) {
      params = params.set('comments', comments);
    }

    return this.httpClient.put<ApiResponse<LabTestingResult>>(`${this.baseUrl}/${id}/update-result`, {}, { params });
  }

  public hardApprove(id: string, comments?: string): Observable<ApiResponse<LabTestingResult>> {
    return this.httpClient.patch<ApiResponse<LabTestingResult>>(`${this.baseUrl}/${id}/hard-approve`, { comments });
  }
}
